import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { saveAs } from 'file-saver';
import { useResizeObserver } from 'usehooks-ts';

import { Table, Modal, Select, Input, Tooltip, Form, Switch, notification, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
    DeleteOutlined,
    QuestionCircleOutlined,
    UsergroupAddOutlined,
    NodeIndexOutlined,
    PrinterOutlined,
    FileZipOutlined,
    FileExcelOutlined,
    CarOutlined,
    ForkOutlined,
    StopOutlined,
    EditOutlined,
} from '@ant-design/icons';

import { usePDF } from '@react-pdf/renderer';
import printJS from 'print-js';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';
import Processing from '@controls/processing';
import IBaseComponentProps from '@components/base-component-props';

import { exception, securityRestriction, warning } from '@extensions/notification';
import { getEnumList, delayAction, getDateTimeLocal, dataURItoBlob } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import CertificatePdf from '@print-forms/handover-takeover-certificate/certificate-pdf';

import { IShipmentJournal } from '@entities/shipment-journal';
import { IUserSession } from '@entities/user-session';
import { IJournalFilter } from '@entities/journal-filter';
import { ICity } from '@entities/city';

import { JournalStatus, enumLabel as journalStatusLabel } from '@enums/journal-status';
import { AreaType } from '@enums/area-type';
import { enumLabel as jobStatusLabel } from '@enums/job-status';
import { Permission, hasPermission } from '@enums/permission';
import { IEnumItem } from '@enums/enum-item';
import { DeliveryOption, enumLabel as deliveryOptionLabel } from '@enums/delivery-option';

import { TruckIcon, AirplaneIcon, PickupIcon } from '@icons/index';

dayjs.extend(utc);

const filterContext: string = 'ShipmentJournals';

const ShipmentJournals = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const initFilter: IJournalFilter = {
        statuses: [JournalStatus.New, JournalStatus.InProcess],
        includeCity: true,
        includeQty: true,
        isArchived: false,
    };

    const { TextArea } = Input;
    const [cancelForm] = Form.useForm();

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IJournalFilter>((s) => s.filters[filterContext]);

    const [api, contextHolder] = notification.useNotification();
    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();

    const [entities, setEntities] = useState<Array<IShipmentJournal>>([]);
    const [currentJournal, setCurrentItem] = useState<IShipmentJournal>();

    const [cities, setCities] = useState<Array<ICity>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [statuses] = useState<Array<IEnumItem>>(getEnumList(JournalStatus, journalStatusLabel));
    const [deliveryOptions] = useState<Array<IEnumItem>>(getEnumList(DeliveryOption, deliveryOptionLabel));

    const [printData, setPrintData] = useState();
    const [startPrint, setStartPrint] = useState(false);
    const [loadingPrintData, setLoadingPrintData] = useState(false);

    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);

    const [pdfInstance, updatePdf] = usePDF();

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('cities', { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('shipmentjournals', { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения журналов отгрузки', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCities(result[0][0] || []);
                setEntities(result[0][1] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (printData) {
            updatePdf(<CertificatePdf printData={printData} />);
            setLoadingPrintData(false);
            setStartPrint(true);
        }
    }, [printData]);

    useEffect(() => {
        if (startPrint && !pdfInstance.loading && pdfInstance.blob) {
            setStartPrint(false);
            setPrintData(undefined);

            const blobURL = URL.createObjectURL(pdfInstance.blob);
            printJS(blobURL);
        }
    }, [startPrint, pdfInstance]);

    const getPrintData = () => {
        if (!currentJournal) return;

        setLoadingPrintData(true);

        serverFetch(`shipmentjournals/${currentJournal.id}/print`, { method: 'GET' })
            .then((data: any) => {
                setPrintData(data);
            })
            .catch((ex: any) => {
                setLoadingPrintData(false);
                exception(api, 'Ошибка печати акта приема-передачи груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const setArchive = () => {
        if (!currentJournal) return;

        serverFetch(`shipmentjournals/${currentJournal.id}/archive`, { method: 'POST' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка архивирования журнала', ex, () => d(userLoaded(undefined)));
            });
    };

    const onDelete = () => {
        if (!currentJournal) return;

        serverFetch(`shipmentjournals/${currentJournal.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления журнала', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRevert = (data: any) => {
        if (!currentJournal) return;

        serverFetch(`shipmentjournals/${currentJournal.id}/cancel`, { method: 'POST', bodyData: data.reason })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
                setOpenCancelModal(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка отмены журнала', ex, () => d(userLoaded(undefined)));
            });
    };

    const onExportConsignmentNote = () => {
        setLoading(true);

        serverFetch(`warehouse/${currentJournal?.id}/consignmentnote/export`, { method: 'GET' })
            .then((data: any) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);

                setLoading(false);
            })
            .catch((ex: any) => {
                setLoading(false);

                exception(api, 'Ошибка выгрузки ТТН', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Изменить',
                        key: 'details',
                        disabled: loading || !currentJournal,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`${resolved.pathname}/${currentJournal?.id}`);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Управление машиной',
                        key: 'add',
                        type: 'primary',
                        disabled: !currentJournal || currentJournal.deliveryOption !== DeliveryOption.Truck,
                        icon: <CarOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            if (currentJournal?.truckId) {
                                navigate(`${resolved.pathname}/${currentJournal?.id}/truck/${currentJournal.truckId}`);
                            } else {
                                navigate(`${resolved.pathname}/${currentJournal?.id}/truck/new`);
                            }
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: loading || !currentJournal || !!currentJournal.truckId,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteShipmentJournal)) {
                                securityRestriction(api, [Permission.DeleteShipmentJournal]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить журнал "${currentJournal?.journalNumber}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Назначить исполнителя',
                        key: 'assignJob',
                        disabled:
                            !currentJournal ||
                            currentJournal.status == JournalStatus.Cancelled ||
                            currentJournal.status == JournalStatus.Completed,
                        icon: <UsergroupAddOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            if (!currentJournal?.truckId) {
                                warning(api, 'Укажите машину на отгрузку в "Управление машиной"');
                                return;
                            }

                            if (currentJournal?.status != JournalStatus.InProcess) {
                                warning(api, 'Необходимо отправить журнал "В работу"');
                                return;
                            }

                            navigate(`/flc/warehouse/performers/${AreaType.Shipment}/${currentJournal?.truckId}`);
                        },
                    },
                    {
                        label: 'Вернуть товар на склад',
                        key: 'cancel',
                        disabled: loading || !currentJournal,
                        icon: <StopOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.RevertShipment)) {
                                securityRestriction(api, [Permission.RevertShipment]);
                                return;
                            }

                            modal.confirm({
                                title: `Вернуть товар на склад из погрузки "${currentJournal?.journalNumber}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => setOpenCancelModal(true),
                            });
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Управление маршрутом',
                        key: 'manageRoutes',
                        disabled: loading || !currentJournal,
                        icon: <ForkOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            navigate(`/flc/warehouse/shipmentjournals/manageoutes/${currentJournal?.id}/${currentJournal?.journalNumber}`);
                        },
                    },
                    {
                        label: 'Маршрутный лист',
                        key: 'routesheet',
                        disabled: loading || !currentJournal,
                        icon: <NodeIndexOutlined />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageWarehouse) &&
                                !hasPermission(userSession.permissions, Permission.PrintDocuments)
                            ) {
                                securityRestriction(api, [Permission.ManageWarehouse, Permission.PrintDocuments]);
                                return;
                            }

                            navigate(`/flc/warehouse/routesheet/${currentJournal?.id}`);
                        },
                    },
                    { isDivider: true },
                    /*  {
                        label: 'Печать акт приема - передачи',
                        key: 'prinCertificate',
                        disabled: loading || !currentJournal || startPrint || currentJournal.status !== JournalStatus.Completed,
                        icon: <PrinterOutlined />,
                        onClick: () => {
                            if (
                                !hasPermission(userSession.permissions, Permission.ManageWarehouse) &&
                                !hasPermission(userSession.permissions, Permission.PrintDocuments)
                            ) {
                                securityRestriction(api, [Permission.ManageWarehouse, Permission.PrintDocuments]);
                                return;
                            }

                            if (currentJournal?.deliveryOption !== DeliveryOption.Truck || currentJournal?.truckId) {
                                getPrintData();
                                return;
                            }

                            modal.confirm({
                                title: `Для журнала "${currentJournal?.journalNumber}" не назначена машина, продолжить?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    getPrintData();
                                },
                            });
                        },
                    }, */
                    {
                        label: 'Скачать ТТН',
                        key: 'exportConsignmentNote',
                        icon: <FileExcelOutlined />,
                        disabled:
                            loading || !currentJournal || currentJournal.status !== JournalStatus.Completed || !currentJournal.truckId,
                        onClick: () => onExportConsignmentNote(),
                    },
                    { isDivider: true },
                    {
                        label: 'В архив',
                        key: 'archive',
                        disabled: loading || !currentJournal || currentJournal.status !== JournalStatus.Completed,
                        icon: <FileZipOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            modal.confirm({
                                title: `Отправить журнал "${currentJournal?.journalNumber}" в архив?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    setArchive();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 130 }}
                            key='journalNumber'
                            placeholder='Журнал'
                            value={filter?.journalNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, journalNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='deliveryOption'
                            placeholder='Вариант доставки'
                            value={filter?.deliveryOption}
                            size='middle'
                            allowClear
                            style={{ width: 170 }}
                            onChange={(value) => d(setFilter({ ...filter, deliveryOption: value }, filterContext))}
                            options={deliveryOptions.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />,
                        <Input
                            style={{ width: 150 }}
                            key='truckNumber'
                            placeholder='Машина'
                            value={filter?.truckNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, truckNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((s) => {
                                return { value: s.id, label: s.name };
                            })}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус журнала'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />,
                        <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isArchived}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, isArchived: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length === 1) {
            const entity = entities.find((o) => o.id === selectedRowKeys[0]);
            setCurrentItem(entity);
        } else {
            setCurrentItem(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<IShipmentJournal> = [
            {
                title: 'Журнал',
                dataIndex: 'journalNumber',
                width: 100,
            },
            {
                title: 'Статус журнала',
                width: 140,
                render: (_, record) => {
                    return <>{journalStatusLabel(record.status)} </>;
                },
            },
            {
                width: 50,
                align: 'center',
                render: (_, record) => {
                    switch (record.deliveryOption) {
                        case DeliveryOption.Truck:
                            return (
                                <Tooltip placement='top' title={deliveryOptionLabel(DeliveryOption.Truck)}>
                                    <TruckIcon className='delivery-option' />
                                </Tooltip>
                            );
                        case DeliveryOption.Pickup:
                            return (
                                <Tooltip placement='top' title={deliveryOptionLabel(DeliveryOption.Pickup)}>
                                    <PickupIcon className='delivery-option' />
                                </Tooltip>
                            );
                        case DeliveryOption.Airplane:
                            return (
                                <Tooltip placement='top' title={deliveryOptionLabel(DeliveryOption.Airplane)}>
                                    <AirplaneIcon className='delivery-option' />
                                </Tooltip>
                            );
                        default:
                            return '';
                    }
                },
            },
            {
                title: 'Машина',
                dataIndex: 'truckNumber',
                width: 150,
            },
            {
                title: 'Отгрузка',
                width: 140,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return getDateTimeLocal(record.departureOn);
                },
            },
            {
                title: 'Исполнитель',
                key: 'performerName',
                dataIndex: 'performerName',
                width: 200,
            },
            {
                title: 'Статус выполнения',
                key: 'jobStatus',
                width: 160,
                render: (_, record) => {
                    return <>{jobStatusLabel(record.jobStatus)} </>;
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span style={{ color: record.totalScannedBoxQty === record.totalBoxQty ? '#228B22' : 'red' }}>
                                {record.totalScannedBoxQty}
                            </span>
                            <span> / </span>
                            <span>{record.totalBoxQty}</span>
                        </>
                    );
                },
            },
            {
                title: 'Города',
                dataIndex: 'cities',
                width: 400,
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={entities}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                    columnWidth: 32,
                }}
                onRow={(record: IShipmentJournal) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px)` }}>
            {modalContextHolder}
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {(loadingPrintData || startPrint) && <Processing title='Печать...' />}

            <Modal
                width={400}
                title='Причина возврата'
                open={openCancelModal}
                okText='ОК'
                closable={false}
                onOk={() => {
                    cancelForm.submit();
                }}
                onCancel={() => {
                    cancelForm.resetFields();
                    setOpenCancelModal(false);
                }}
            >
                <Form colon={false} wrapperCol={{ span: 24 }} form={cancelForm} onFinish={onRevert}>
                    <Form.Item name='reason' required rules={[{ required: true, message: 'Укажите причину возврата' }]}>
                        <TextArea rows={6} />
                    </Form.Item>
                </Form>
            </Modal>

            {contextHolder}
        </div>
    );
};

export default ShipmentJournals;
